import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconFingerprint, IconLock, IconMail } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useState } from "react";
import z from "zod";
import { authClient } from "~/lib/auth-client";
import { logger } from "~/lib/logger";

const schema = z.object({
  email: z.email({ message: "Invalid email" }),
  password: z.string().min(4, { message: "Password too small" }),
});

export const Route = createFileRoute("/_auth/login")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate({ from: "/" });
  const [error, setError] = useState<string>();
  const { isPending } = authClient.useSession();

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: zod4Resolver(schema),
  });

  async function signIn(values: z.infer<typeof schema>) {
    await authClient.signIn.email(values, {
      onSuccess: () => {
        navigate({ to: "/" });
        logger.debug("Sign in successful");
      },
      onError: (error) => {
        logger.error(error);
        setError(error.error.message || error.error.statusText);
      },
    });
  }

  if (isPending) {
    return (
      <Center h="100dvh">
        <Text>Loading...</Text>
      </Center>
    );
  }

  return (
    <Box
      style={{
        minHeight: "100dvh",
        background: "radial-gradient(circle at center, var(--mantine-color-dark-8) 0%, var(--mantine-color-dark-9) 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
      <Container size={650} my={40}>
        <Paper
          withBorder
          shadow="xl"
          p={60}
          w={500}
          radius={24}
          style={{
            backgroundColor: 'rgba(30, 30, 30, 0.85)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Stack align="center" gap={10} mb={40}>
            <ThemeIcon size={70} radius={70} variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
              <IconFingerprint size={38} stroke={1.5} />
            </ThemeIcon>
            <Title ta="center" order={1} fw={900} fz={32}>
              Welcome Back
            </Title>
            <Text c="dimmed" size="md" ta="center">
              Sign in to your Perforia CRM account
            </Text>
          </Stack>

          <form onSubmit={form.onSubmit(signIn, () => setError(undefined))}>
            {error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error" mb="xl" variant="light" radius="md">
                {error}
              </Alert>
            )}

            <TextInput
              label="Email"
              placeholder="you@email.com"
              required
              size="lg"
              radius="md"
              mb="md"
              leftSection={<IconMail size={20} stroke={1.5} />}
              {...form.getInputProps("email")}
            />
            <PasswordInput
              label="Password"
              placeholder="Your password"
              required
              size="lg"
              radius="md"
              leftSection={<IconLock size={20} stroke={1.5} />}
              {...form.getInputProps("password")}
            />

            <Button fullWidth mt={40} size="lg" radius="md" type="submit" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
              Sign in
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
